import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import client, { unwrap } from "@/api/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Building2, CalendarDays, FileText, LayoutGrid, List, MoreVertical, Search, Tag } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import PageHero from "@/components/layout/PageHero";
import ImportFromPdfDialog from "@/components/catalogues/ImportFromPdfDialog";
import { productMaterial, productTypeLabel } from "@/components/ProductCard";
import useCatalogueProducts from "@/hooks/useCatalogueProducts";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { titleCase } from "@/lib/format";
import { CATALOGUES_HERO_IMAGE, productImageFor } from "@/lib/stockImages";


const IMPORT_IN_PROGRESS = ["QUEUED", "EXTRACTING", "PUBLISHING"];

function ImportsList() {
  const { data: imports = [] } = useQuery({
    queryKey: ["catalogue-imports"],
    queryFn: () => client.get("/catalogues/imports").then(unwrap),
    refetchInterval: (query) => ((query.state.data || []).some((i) => IMPORT_IN_PROGRESS.includes(i.status)) ? 3000 : false),
  });
  const open = imports.filter((i) => i.status !== "PUBLISHED");
  if (!open.length) return null;
  return (
    <section data-testid="catalogue-imports-list">
      <p className="text-[11px] font-semibold tracking-widest text-muted-foreground uppercase mb-3">PDF imports in progress</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {open.map((i) => (
          <Link key={i._id} to={`/catalogues/imports/${i._id}`} data-testid={`catalogue-import-card-${i._id}`}>
            <div className="bg-card border border-dashed border-border hover:border-accent rounded-2xl p-4 transition-colors h-full">
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="font-heading text-lg text-foreground truncate">{i.versionName}</p>
                <StatusBadge status={i.status} />
              </div>
              <p className="text-sm text-muted-foreground truncate">{i.companyName}</p>
              {i.status === "EXTRACTING" && i.progress?.total > 0 && (
                <div className="mt-3">
                  <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${(i.progress.done / i.progress.total) * 100}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">Step {i.progress.done} of {i.progress.total}</p>
                </div>
              )}
              {i.status === "FAILED" && <p className="text-xs text-danger mt-2 line-clamp-2">{i.error}</p>}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

const ALL = "all";

// Chips sit on fabric photos, so they get a solid backing and only the text carries the colour.
const STATUS_CHIP = {
  APPROVED: "text-success",
  PENDING_APPROVAL: "text-[hsl(41_60%_32%)]",
  CHANGES_REQUESTED: "text-[hsl(41_60%_32%)]",
  REJECTED: "text-danger",
};
const statusLabel = (s) => (s === "PENDING_APPROVAL" ? "Pending" : titleCase(s || ""));

const SORTS = {
  updated: { label: "Latest updated", fn: (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt) },
  name: { label: "Name (A–Z)", fn: (a, b) => a.title.localeCompare(b.title) },
  products: { label: "Most products", fn: (a, b) => b.productCount - a.productCount },
};

const catalogueTitle = (c) => c.versionName || c.catalogueReference;
const formatDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
const topValues = (values, n) => {
  const counts = {};
  values.filter(Boolean).forEach((v) => (counts[v] = (counts[v] || 0) + 1));
  return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, n).map(([v]) => v);
};

function StatusChip({ status }) {
  return (
    <span className={cn("rounded-full border border-border bg-card/95 shadow-sm px-2.5 py-0.5 text-[10px] font-semibold tracking-wider uppercase", STATUS_CHIP[status] || "text-muted-foreground")}>
      {statusLabel(status)}
    </span>
  );
}

function CatalogueMenu({ c }) {
  const navigate = useNavigate();
  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(c.catalogueReference);
      toast.success(`Copied ${c.catalogueReference}`);
    } catch {
      toast.error("Couldn't copy the reference");
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`More actions for ${c.title}`}
          onClick={(e) => e.preventDefault()}
          className="p-1 -mr-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary shrink-0"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => navigate(`/catalogues/${c._id}`)}>Open catalogue</DropdownMenuItem>
        {c.catalogueReference && <DropdownMenuItem onClick={copyRef}>Copy reference</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CatalogueCard({ c }) {
  return (
    <div data-testid={`catalogue-card-${c._id}`} className="group bg-card border border-border rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
      <Link to={`/catalogues/${c._id}`} className="block relative aspect-[16/9] overflow-hidden bg-secondary">
        <img src={c.cover} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
        <span className="absolute top-3 right-3"><StatusChip status={c.status} /></span>
        {c.isMaster && (
          <span className="absolute top-3 left-3 rounded-full bg-card/90 backdrop-blur px-2.5 py-0.5 text-[10px] font-semibold tracking-wider uppercase text-foreground/80">
            Master
          </span>
        )}
      </Link>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/catalogues/${c._id}`} className="font-heading text-lg leading-snug text-foreground hover:text-primary line-clamp-1">
            {c.title}
          </Link>
          <CatalogueMenu c={c} />
        </div>
        <p className="text-sm text-muted-foreground truncate">{c.companyName}</p>
        {c.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {c.tags.map((t) => (
              <span key={t} className="rounded-full border border-border px-2.5 py-0.5 text-[10px] font-medium tracking-wider uppercase text-foreground/70 max-w-full truncate">
                {t}
              </span>
            ))}
          </div>
        )}
        <div className="mt-4 pt-3 border-t border-border flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" /> {c.productCount ?? "…"} product{c.productCount === 1 ? "" : "s"}
          </span>
          <span className="flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" /> Updated {formatDate(c.updatedAt)}
          </span>
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ icon: Icon, value, onChange, options, allLabel, testId, format = (v) => v }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="w-full sm:w-[180px] rounded-lg bg-card">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
          <SelectValue />
        </div>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{format(o)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function CataloguesPage() {
  const { hasPermission } = useAuth();
  const canUpload = hasPermission("catalogue:upload");
  const { catalogues, products, productsLoading } = useCatalogueProducts();
  const [search, setSearch] = useState("");
  const [company, setCompany] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [type, setType] = useState(ALL);
  const [sort, setSort] = useState("updated");
  const [view, setView] = useState("grid");

  // Products carry their catalogue's reference, which links them back for counts, tags and a cover photo.
  const enriched = useMemo(() => {
    const byRef = {};
    products.forEach((p) => (byRef[p.catalogueReference] ||= []).push(p));
    return catalogues.map((c) => {
      const items = byRef[c.catalogueReference] || [];
      const types = topValues(items.map(productTypeLabel), 5);
      return {
        ...c,
        title: catalogueTitle(c),
        productCount: productsLoading ? null : items.length,
        types,
        tags: [...types.slice(0, 2), ...topValues(items.map(productMaterial), 1)],
        cover: items.find((p) => p.imageUrls?.length)?.imageUrls[0] || productImageFor(c._id),
      };
    });
  }, [catalogues, products, productsLoading]);

  const companies = useMemo(() => [...new Set(catalogues.map((c) => c.companyName))].sort(), [catalogues]);
  const statuses = useMemo(() => [...new Set(catalogues.map((c) => c.status))], [catalogues]);
  const types = useMemo(() => [...new Set(enriched.flatMap((c) => c.types))].sort(), [enriched]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return enriched
      .filter((c) => company === ALL || c.companyName === company)
      .filter((c) => status === ALL || c.status === status)
      .filter((c) => type === ALL || c.types.includes(type))
      .filter((c) => !term || [c.title, c.companyName, c.brand, c.catalogueReference].some((v) => v?.toLowerCase().includes(term)))
      .sort(SORTS[sort].fn);
  }, [enriched, company, status, type, search, sort]);

  const filtersActive = search || company !== ALL || status !== ALL || type !== ALL;
  const clearFilters = () => {
    setSearch("");
    setCompany(ALL);
    setStatus(ALL);
    setType(ALL);
  };

  return (
    <div data-testid="catalogues-page" className="space-y-6">
      <PageHero
        kpiOverlap={false}
        image={CATALOGUES_HERO_IMAGE}
        tagline="Curated collections for every space."
        title="Catalogues"
        subtitle="Your uploads plus shared master catalogues approved by the platform."
        action={
          canUpload && <ImportFromPdfDialog />
        }
      />

      {canUpload && <ImportsList />}

      <section className="bg-card border border-border rounded-2xl p-4 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1 lg:max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            data-testid="catalogues-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by catalogue, company, reference…"
            className="w-full rounded-full border border-border bg-secondary/70 pl-10 pr-4 py-2.5 text-sm placeholder:text-muted-foreground focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
          <FilterSelect testId="catalogues-company-filter" icon={Building2} value={company} onChange={setCompany} options={companies} allLabel="All companies" />
          <FilterSelect testId="catalogues-status-filter" icon={Tag} value={status} onChange={setStatus} options={statuses} allLabel="All statuses" format={statusLabel} />
          {types.length > 0 && (
            <FilterSelect testId="catalogues-type-filter" icon={LayoutGrid} value={type} onChange={setType} options={types} allLabel="All types" />
          )}
        </div>
        {filtersActive && (
          <button data-testid="catalogues-clear-filters" onClick={clearFilters} className="text-sm font-medium text-accent hover:underline text-left lg:ml-2">
            Clear filters
          </button>
        )}
        <div className="hidden md:flex items-center gap-1 lg:ml-auto rounded-lg border border-border p-0.5" role="group" aria-label="View">
          {[["grid", LayoutGrid], ["list", List]].map(([key, Icon]) => (
            <button
              key={key}
              aria-label={`${key} view`}
              aria-pressed={view === key}
              data-testid={`catalogues-view-${key}`}
              onClick={() => setView(key)}
              className={cn("p-2 rounded-md transition-colors", view === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary")}
            >
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>
      </section>

      <div className="flex items-center justify-between gap-3">
        <p className="font-medium text-foreground" data-testid="catalogues-count">
          {visible.length} catalogue{visible.length === 1 ? "" : "s"}
        </p>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="catalogues-sort" className="w-[200px] rounded-lg bg-card">
            <span className="text-muted-foreground mr-1">Sort:</span> <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(SORTS).map(([key, s]) => <SelectItem key={key} value={key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {view === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
          {visible.map((c) => <CatalogueCard key={c._id} c={c} />)}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <ul className="divide-y divide-border">
            {visible.map((c) => (
              <li key={c._id}>
                <Link to={`/catalogues/${c._id}`} data-testid={`catalogue-row-${c._id}`} className="flex items-center gap-4 px-4 py-3 hover:bg-secondary/40 transition-colors">
                  <img src={c.cover} alt="" loading="lazy" className="h-14 w-20 rounded-lg object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{c.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {c.companyName} · <span className="font-mono-spec">{c.catalogueReference}</span>
                    </p>
                  </div>
                  <span className="hidden lg:block w-48 text-sm text-foreground/80 truncate">{c.types.join(", ") || "—"}</span>
                  <span className="hidden md:block w-24 text-sm text-foreground/80">{c.productCount ?? "…"} products</span>
                  <span className="hidden md:block w-32 text-sm text-muted-foreground">{formatDate(c.updatedAt)}</span>
                  <StatusChip status={c.status} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {visible.length === 0 && (
        <div className="bg-card border border-dashed border-border rounded-2xl py-14 text-center">
          <p className="text-sm text-muted-foreground">
            {catalogues.length === 0 ? "No catalogues yet." : "No catalogues match these filters."}
          </p>
          {filtersActive && <button onClick={clearFilters} className="mt-2 text-sm font-medium text-accent hover:underline">Clear filters</button>}
        </div>
      )}
    </div>
  );
}
